import { Box, styled } from "@material-ui/core";
import { memo, useState } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import type { ChoiceOption } from "../onboarding-chat";
import { CheckIcon, Icon, isKnownIcon } from "./icons";

const Stack = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginTop: 4,
});

const Inline = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  marginTop: 4,
});

const Option = styled("button")({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 14px",
  minHeight: 44,
  border: `1.5px solid ${EPOCUS_BORDER}`,
  borderRadius: 10,
  cursor: "pointer",
  backgroundColor: EPOCUS_WHITE,
  transition: "all .14s",
  font: "inherit",
  fontSize: rem(13.5),
  color: EPOCUS_TEXT_PRIMARY,
  textAlign: "left",
  width: "100%",
  "&:hover:not(:disabled)": {
    borderColor: EPOCUS_PRIMARY,
    backgroundColor: EPOCUS_SELECTED_BG,
  },
  "&:active:not(:disabled)": { transform: "scale(.98)" },
  "&:disabled": { opacity: 0.55, cursor: "default" },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
});

const GHOST = {
  borderStyle: "dashed",
  color: EPOCUS_TEXT_SECONDARY,
} as const;

const SUBMIT = {
  backgroundColor: EPOCUS_PRIMARY,
  color: EPOCUS_WHITE,
  border: "none",
  fontWeight: 600,
  justifyContent: "center",
} as const;

const CHIP = { width: "auto", padding: "9px 14px", borderRadius: 999 } as const;

const CHIP_SELECTED = {
  ...CHIP,
  borderColor: EPOCUS_PRIMARY,
  backgroundColor: EPOCUS_SELECTED_BG,
  color: EPOCUS_PRIMARY,
  fontWeight: 500,
} as const;

const IconSlot = styled("span")({
  width: 30,
  height: 30,
  borderRadius: 8,
  flex: "none",
  backgroundColor: EPOCUS_SELECTED_BG,
  color: EPOCUS_PRIMARY,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const RESERVED_SLOT = { backgroundColor: "transparent" } as const;

const Tick = styled("span")({
  width: 15,
  height: 15,
  borderRadius: "50%",
  border: `1.5px solid ${EPOCUS_BORDER}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: EPOCUS_WHITE,
  flex: "none",
});

const TICK_SELECTED = {
  backgroundColor: EPOCUS_PRIMARY,
  borderColor: EPOCUS_PRIMARY,
} as const;

const styleFor = (option: ChoiceOption) =>
  option.style === "ghost" ? GHOST : undefined;

const SingleChoice: FC<{
  options: ChoiceOption[];
  live: boolean;
  onPick: (option: ChoiceOption) => void;
}> = ({ options, live, onPick }) => {
  const anyIcon = options.some((option) => isKnownIcon(option.icon));

  return (
    <Stack>
      {options.map((option) => (
        <Option
          key={option.id}
          type="button"
          disabled={!live}
          style={styleFor(option)}
          onClick={() => onPick(option)}
        >
          {anyIcon && (
            <IconSlot
              style={isKnownIcon(option.icon) ? undefined : RESERVED_SLOT}
            >
              {isKnownIcon(option.icon) && <Icon name={option.icon} />}
            </IconSlot>
          )}
          <span>{option.label}</span>
        </Option>
      ))}
    </Stack>
  );
};

const MultiChoice: FC<{
  options: ChoiceOption[];
  initial: string[];
  submitLabel: string;
  live: boolean;
  onSubmit: (options: ChoiceOption[]) => void;
}> = ({ options, initial, submitLabel, live, onSubmit }) => {
  const [selected, setSelected] = useState<string[]>(initial);

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );

  return (
    <>
      <Inline>
        {options.map((option) => {
          const isSelected = selected.includes(option.id);
          return (
            <Option
              key={option.id}
              type="button"
              disabled={!live}
              aria-pressed={isSelected}
              style={isSelected ? CHIP_SELECTED : CHIP}
              onClick={() => toggle(option.id)}
            >
              <Tick style={isSelected ? TICK_SELECTED : undefined}>
                {isSelected && <CheckIcon size={9} />}
              </Tick>
              <span>{option.label}</span>
            </Option>
          );
        })}
      </Inline>
      <Stack>
        <Option
          type="button"
          disabled={!live || selected.length === 0}
          style={SUBMIT}
          onClick={() =>
            onSubmit(options.filter((option) => selected.includes(option.id)))
          }
        >
          <span>{submitLabel}</span>
        </Option>
      </Stack>
    </>
  );
};

export const ChoiceGroup = memo<{
  mode: "single" | "multi";
  options: ChoiceOption[];
  selected?: string[];
  submitLabel?: string;
  live: boolean;
  onRespond: (options: ChoiceOption[]) => void;
}>(({ mode, options, selected, submitLabel, live, onRespond }) => {
  const [answered, setAnswered] = useState(false);

  const respondOnce = (picked: ChoiceOption[]) => {
    if (answered) return;
    setAnswered(true);
    onRespond(picked);
  };

  return mode === "multi" ? (
    <MultiChoice
      options={options}
      initial={selected ?? []}
      submitLabel={submitLabel ?? "Continue"}
      live={live && !answered}
      onSubmit={respondOnce}
    />
  ) : (
    <SingleChoice
      options={options}
      live={live && !answered}
      onPick={(option) => respondOnce([option])}
    />
  );
});
ChoiceGroup.displayName = "ChoiceGroup";
